import { ROUTES } from '@/constants/constants';
import { Context } from '@/context/appContext';
import { logEvent } from '@/helpers/eventLogger';
import { useNotifications } from '@/hooks/useNotifications';
import { AppStateActions } from '@/reducers/appReducer';
import { useCallback, useContext, useMemo } from 'react';
import type { DropzoneOptions, FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

/** Client-side upload ceiling, in MB. The backend enforces its own. */
export const MAX_FILE_SIZE = 100;

/**
 * Hands a chosen PDF over to the editor.
 *
 * This is the whole "upload" the landing page does: the file is validated, put into app state and
 * the editor is opened. The actual HTTP upload happens later, in
 * `components/PdfEditor/hooks/useDocumentUpload.ts`, once the editor has mounted.
 *
 * No JSX and no dropzone coupling, so the page-level drop target, the dropzone card and every
 * "Choose a file" button in the header and footer can share one implementation.
 */
export function usePdfHandoff() {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.pdfUploadScreen' });
  const { dispatch } = useContext(Context);
  const { notifyError } = useNotifications();
  const navigate = useNavigate();

  const handleFiles = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length < 1) return;

      const document = acceptedFiles[0];
      if (document.size > MAX_FILE_SIZE * 1_000_000) {
        notifyError(t('error.fileSize'));
        return;
      }
      if (!document.name.toLowerCase().endsWith('.pdf')) {
        notifyError(t('error.fileType'));
        return;
      }

      // Clear any state retained from a previously open document (e.g. parsed text blocks
      // kept across editor-results navigation) so it doesn't leak onto the new upload.
      // SET_DOCUMENT(null) triggers a full RESET_STATE in the reducer.
      dispatch({ type: AppStateActions.SET_DOCUMENT, payload: { document: null } });

      // Store the file and navigate. The editor owns the upload from here.
      dispatch({ type: AppStateActions.SET_DOCUMENT, payload: { document } });
      dispatch({ type: AppStateActions.SET_DOCUMENT_LOAD_STATE, payload: { isDocumentLoaded: false } });
      navigate(ROUTES.editNew(i18n.language));
    },
    [dispatch, i18n.language, navigate, notifyError, t],
  );

  const handleRejected = useCallback(
    (rejectedFiles: FileRejection[]) => {
      const error = rejectedFiles[0].errors[0];
      const { name, size } = rejectedFiles[0].file;

      if (error.code === 'file-invalid-type') {
        logEvent({ logType: 'ERROR', eventType: 'WRONG_FILE_TYPE_UPLOADED', trace: { name, size } });
      } else if (error.code === 'file-too-large') {
        logEvent({ logType: 'ERROR', eventType: 'WRONG_FILE_SIZE_UPLOADED', trace: { name, size } });
      }

      const message =
        error.code === 'file-invalid-type'
          ? t('error.fileType')
          : error.code === 'file-too-large'
            ? t('error.fileSize')
            : error.message;
      notifyError(message);
    },
    [notifyError, t],
  );

  /** Spread into `useDropzone` so every drop target accepts exactly the same files. */
  const dropzoneOptions = useMemo<DropzoneOptions>(
    () => ({
      onDrop: handleFiles,
      onDropRejected: handleRejected,
      accept: { 'application/pdf': ['.pdf'] },
      multiple: false,
      maxSize: MAX_FILE_SIZE * 1_000_000,
      maxFiles: 1,
    }),
    [handleFiles, handleRejected],
  );

  return { handleFiles, handleRejected, dropzoneOptions };
}
